import { FC, useEffect, useRef, useState } from 'react';
import { useLayout } from '../../../_metronic/layout/core';
import { KTSVG } from '../../../_metronic/helpers';
import { useHistory, useParams } from 'react-router-dom';
import { showErrorAlert } from '../../helpers/alert';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import * as Yup from 'yup'
import { moderationTeamCheck } from '../../services/teamAdminService';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../../../setup';
import { UserModel } from '../../modules/auth/models/UserModel';
import { createUserTeamForFinalUser } from '../../services/teamService';
import { Assignment } from './models/UserTeamModel';
import PhoneInput from 'react-phone-input-2';
import { SectionRole } from '../manageAdmins/components/modals/AddMemberModal';

const AddMemberTeamPage: FC = () => {
    const user = useSelector<RootState>(
        ({ auth }) => auth.user,
        shallowEqual
    ) as UserModel
    const { config, setLayout } = useLayout()
    const restoreRef = useRef(config)
    const formikRef = useRef<any>(null)
    const history = useHistory()
    const { userId } = useParams<{ userId:string }>()
    const [teamId, setTeamId] = useState(0)
    const [initialValues, setInitialValues] = useState({
        id: 0,
        first_name: '',
        last_name: '',
        relation: '',
        organization: '',
        phone_number: '',
        email: ''
    })

    const [sections, setSections]= useState<any[]>([
        {
            module: 0,
            moduleName: "Profile Management",
            moduleSlug: "profile",
            onlyOwnRecords: false,
            canCreate: false, 
            canDelete: false, 
            canEdit: false,
            canViewSensitiveInfo: false
        },
        {
            module: 1,
            moduleName: "Experience Management",
            moduleSlug: "experiences",
            onlyOwnRecords: false,
            canCreate: false, 
            canDelete: false, 
            canEdit: false,
            canViewSensitiveInfo: false
        },
        {
            module: 2,
            moduleName: "Calendar Management",
            moduleSlug: "calendar",
            onlyOwnRecords: false,
            canCreate: false, 
            canDelete: false, 
            canEdit: false,
            canViewSensitiveInfo: false
        },
        {
            module: 3,
            moduleName: "Mastermind Circle Management",
            moduleSlug: "mastermind",
            onlyOwnRecords: false,
            canCreate: false, 
            canDelete: false, 
            canEdit: false,
            canViewSensitiveInfo: false
        }
    ]);

    useEffect(() => {
        restoreRef.current = config
        setLayout({
            ...config,
            header: { ...config.header, display: true },
            aside: { ...config.aside, display: true },
            toolbar: { ...config.toolbar, display: false },
            footer: { ...config.footer, display: true },
        });
        // buscar team
        searchTeam();
        return () => {
            setLayout(restoreRef.current)
        }
    }, [])

    const validationSchema = Yup.object({
        first_name: Yup.string().required("First name is required").max(100, "Max 100 characters"),
        last_name: Yup.string().required("Last name is required").max(100, "Max 100 characters"),
        relation: Yup.string().required("Relation is required").max(100, "Max 100 characters"),
        organization: Yup.string().required("Organization is required").max(100, "Max 100 characters"),
        phone_number: Yup.string().matches(/^[0-9()+\-\s]{7,15}$/,'Invalid phone number').required('Phone is required'),
        email: Yup.string().email('Wrong email format').min(3, 'Minimum 3 symbols').max(50, 'Maximum 50 symbols').required('Email is required')
    });

    const searchTeam = async () => {
        const teams = await moderationTeamCheck({ user_id: userId })
        let team = teams?.length > 0 ? teams[0] : null;
        if(team?.id && team?.isTargetUserOwner){
            setTeamId(team.id)
        }else{
            history.goBack();
        }
    }

    const handleSubmit = async (values: any) => {
        const _sections = [...sections];
        const hasAnyPermission = _sections.some(s => s.canCreate || s.canEdit || s.canDelete || s.canViewSensitiveInfo);
        if (!hasAnyPermission) {
            showErrorAlert({ title: 'Validation Error', message: 'Please select at least one permission for the team member.' });
            return;
        }

        let _body = { 
            ...values,
            permissions:_sections.reduce((acc, s) => {
                                        const canView = Boolean(s.canViewSensitiveInfo || s.canCreate || s.canEdit || s.canDelete);
                                        acc[s.moduleSlug] = {
                                            can_view: canView,
                                            can_view_sensitive_info: Boolean(s.canViewSensitiveInfo),
                                            can_edit: Boolean(s.canEdit),
                                            can_create: Boolean(s.canCreate),
                                            can_delete: Boolean(s.canDelete),
                                        };
                                        return acc;
                                    }, {})
        };
        let resp = await createUserTeamForFinalUser(teamId,_body);
        if(resp?.success){
            showErrorAlert({
                title: 'Success',
                message: 'Team member added successfully.',
                onConfirm: () => history.goBack()
            });
        }
    }


    const handleSectionChange = (moduleId: number, changes: Partial<any>) => {
        setSections(prev =>
            prev.map(section =>
                section.module === moduleId
                ? { ...section, ...changes }
                : section
            )
        )
    }

    return (
        <div className="my-team-page team-form">
            {/* Title */}
            <h1 className="my-team-page-title">My Team</h1>

            {/* Content */}
            <div className="team-form-content">
                <div className="team-form-section">
                    <div className="team-form-section-title">
                        Add team member
                    </div>

                    {/* Inputs */}
                    <Formik innerRef={formikRef} initialValues={initialValues} validationSchema={validationSchema} validateOnMount={true} onSubmit={handleSubmit}>
                        {(formik) => {
                            return (<Form className='d-flex flex-column gap-10'>
                                <div className="team-form-fields">
                                    <div className="team-form-row">
                                        <div className="form-field">
                                            <label>Name</label>
                                            <div className="tap-add-experience-2-input-line">
                                                <input className="tap-add-experience-2-input" name="first_name" value={formik.values.first_name} onChange={formik.handleChange} maxLength={100} />
                                            </div>
                                            <ErrorMessage name="first_name" component="div" className="tap-add-experience-text text-danger" />
                                        </div>

                                        <div className="form-field">
                                            <label>Surname</label>
                                            <div className="tap-add-experience-2-input-line">
                                                <input className="tap-add-experience-2-input" name="last_name" value={formik.values.last_name} onChange={formik.handleChange} maxLength={100} />
                                            </div>
                                            <ErrorMessage name="last_name" component="div" className="tap-add-experience-text text-danger" />
                                        </div>
                                    </div>

                                    <div className="team-form-row">
                                        <div className="form-field">
                                            <label>Relation (PA, EA, Agent, or other)</label>
                                            <div className="tap-add-experience-2-input-line">
                                                <input className="tap-add-experience-2-input" name="relation" value={formik.values.relation} onChange={formik.handleChange} maxLength={100} />
                                            </div>
                                            <ErrorMessage name="relation" component="div" className="tap-add-experience-text text-danger" />
                                        </div>

                                        <div className="form-field">
                                            <label>Organization</label>
                                            <div className="tap-add-experience-2-input-line">
                                                <input className="tap-add-experience-2-input" name="organization" value={formik.values.organization} onChange={formik.handleChange} maxLength={100} />
                                            </div>
                                            <ErrorMessage name="organization" component="div" className="tap-add-experience-text text-danger" />
                                        </div>
                                    </div>

                                    <div className="team-form-row">
                                        <div className="form-field">
                                            <label>Phone Number</label>
                                            <div className="tap-add-experience-2-input-line">
                                                <Field name='phone_number'>
                                                    {({ field, form }: any) => (
                                                        <PhoneInput
                                                        country='us'
                                                        placeholder='+1'
                                                        containerClass='profile-input'

                                                        value={field.value || ''}
                                                        onChange={(val: string) => form.setFieldValue(field.name, val)}
                                                        onBlur={() => form.setFieldTouched(field.name, true)}
                                                        />
                                                    )}
                                                </Field>
                                            </div>
                                            <ErrorMessage name="phone_number" component="div" className="tap-add-experience-text text-danger" />
                                        </div>

                                        <div className="form-field">
                                            <label>Email</label>
                                            <div className="tap-add-experience-2-input-line">
                                                <input className="tap-add-experience-2-input" name="email" value={formik.values.email} onChange={formik.handleChange} maxLength={100} />
                                            </div>
                                            <ErrorMessage name="email" component="div" className="tap-add-experience-text text-danger" />
                                        </div>
                                    </div>
                                </div>

                                {/* Assignment */}
                                <div className="w-100 d-flex flex-row" style={{ overflow: 'auto' }}>
                                    <div className="d-flex flex-column w-100">
                                        <span className='admin-roles-users__header'>Sections</span>
                                        {(sections ?? []).map(section =>
                                            <SectionRole item={section} key={`item-role-admin-${section.module}`} handleClick={()=>{}} />
                                        )}
                                    </div>
                                    <div className='d-flex flex-column w-100' style={{ minWidth: '120px' }}>
                                        <span className='admin-roles-users__header'>All actions (create/edit/delete)</span>
                                        {(sections ?? []).map(section => (
                                            <div key={`item-role-admin-${section.module}-2`} className='admin-roles-users__row pf-checkbox'>
                                            <input type="checkbox" className="pf-check" checked={section.canCreate && section.canDelete && section.canEdit}
                                                onChange={(e) =>
                                                handleSectionChange(section.module, {
                                                    canCreate: e.target.checked,
                                                    canEdit: e.target.checked,
                                                    canDelete: e.target.checked,
                                                    onlyOwnRecords: !e.target.checked,
                                                    ...(e.target.checked ? { canViewSensitiveInfo: true } : {}),
                                                })}
                                            />
                                            </div>
                                        ))}
                                    </div>
                                    <div className="d-flex flex-column w-100" style={{ minWidth: '120px' }}>
                                        <span className='admin-roles-users__header'>View Sensitive Data</span>
                                        {(sections ?? []).map(
                                            section => (
                                                <div key={`item-role-admin-${section.module}-5`} className='admin-roles-users__row pf-checkbox'>
                                                <input type="checkbox" className="pf-check" checked={section?.canViewSensitiveInfo}
                                                    onChange={(e) =>
                                                        handleSectionChange(section.module, {
                                                            canViewSensitiveInfo: e.target.checked,
                                                            ...(!e.target.checked
                                                                ? { canCreate: false, canEdit: false, canDelete: false, onlyOwnRecords: true }
                                                                : {}),
                                                        })
                                                    }
                                                />
                                                </div>
                                            ))}
                                    </div>
                                </div>

                                {/* Footer */}
                                <div className="team-form-footer">
                                    <div role='button' onClick={()=>history.goBack()} className={`tap-add-experience-2-btn-secondary`}>
                                        cancel
                                    </div>

                                    <button type='submit' className="tap-add-experience-2-btn-main">
                                        <div className="tap-host-button-text tap-flex-center">
                                            Add team member &nbsp;<KTSVG path='/media/svg/nobilis/teams_and_partner/waitlist_nb_btn_icon_host_experience_btn.svg' />
                                        </div>
                                    </button>
                                </div>
                            </Form>);
                        }}
                    </Formik>
                </div>
            </div>
        </div>
    );
}

export { AddMemberTeamPage }